"use client";

import Breadcrumb from "@/components/Common/Breadcrumb";
import { Button } from "@/components/ui/button";
import { getImagePath } from "@/lib/utils";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import { useSearchParams, useRouter } from "next/navigation";
import ReCAPTCHA from "react-google-recaptcha";
import { API_BASE, NEXT_PUBLIC_RECAPTCHA_SITE_KEY } from "@/components/constants/general";
import { useTranslations } from "next-intl";
import { FaRegCalendarAlt } from "react-icons/fa";
import { trackRegistrationFormSubmitted, trackRegistrationPaymentInitiated } from "@/lib/analytics";

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const GST_REGEX = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_REGEX = /^[6-9]\d{9}$/;

const formatYMD = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const getDobBounds = () => {
  const now = new Date();
  const max = new Date(now.getFullYear() - 18, now.getMonth(), now.getDate());
  const min = new Date(now.getFullYear() - 100, now.getMonth(), now.getDate());
  return { max: formatYMD(max), min: formatYMD(min) };
};

// Mask free text into DD-MM-YYYY as the user types (digits only, dashes auto).
const maskDate = (raw: string): string => {
  const d = raw.replace(/\D/g, "").slice(0, 8);
  const parts: string[] = [];
  if (d.length >= 1) parts.push(d.slice(0, 2));
  if (d.length >= 3) parts.push(d.slice(2, 4));
  if (d.length >= 5) parts.push(d.slice(4, 8));
  return parts.join("-");
};

// Convert a complete DD-MM-YYYY string to YYYY-MM-DD, or null if not a real
// calendar date (rejects things like 31-02-2020).
const ddmmyyyyToIso = (s: string): string | null => {
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(s);
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const iso = `${yyyy}-${mm}-${dd}`;
  const dt = new Date(`${iso}T00:00:00`);
  if (
    Number.isNaN(dt.getTime()) ||
    dt.getFullYear() !== Number(yyyy) ||
    dt.getMonth() + 1 !== Number(mm) ||
    dt.getDate() !== Number(dd)
  ) {
    return null;
  }
  return iso;
};

const isoToDisplay = (iso: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.split("-").reverse().join("-") : "";

// Date field that supports BOTH typing (DD-MM-YYYY) and the native calendar
// picker. The form value is always kept as YYYY-MM-DD ("" when incomplete).
function DateField({
  value,
  onChange,
  onBlur,
  min,
  max,
  placeholder = "DD-MM-YYYY",
  className = "",
}: {
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  min?: string;
  max?: string;
  placeholder?: string;
  className?: string;
}) {
  const [text, setText] = useState(() => isoToDisplay(value));

  // Reflect external value changes (form reset, or a calendar pick) into the
  // text box — but don't clobber what the user is mid-typing.
  useEffect(() => {
    const display = isoToDisplay(value);
    setText((prev) => (ddmmyyyyToIso(prev) === value ? prev : display));
  }, [value]);

  const handleText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = maskDate(e.target.value);
    setText(masked);
    onChange(ddmmyyyyToIso(masked) ?? "");
  };

  return (
    <div className="relative">
      <input
        type="text"
        inputMode="numeric"
        maxLength={10}
        value={text}
        onChange={handleText}
        onBlur={onBlur}
        placeholder={placeholder}
        className={`pr-12 ${className}`}
      />
      <FaRegCalendarAlt className="absolute right-3 top-1/2 -translate-y-1/2 text-[#575757] pointer-events-none" />
      {/* Native date input over the icon — clicking it opens the calendar. */}
      <input
        type="date"
        aria-label="Open date picker"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => {
          const el = e.currentTarget as HTMLInputElement & {
            showPicker?: () => void;
          };
          if (typeof el.showPicker === "function") {
            try {
              el.showPicker();
            } catch {
              /* showPicker can throw if not allowed; fall back to default */
            }
          }
        }}
        onBlur={onBlur}
        className="absolute right-0 top-0 h-full w-12 opacity-0 cursor-pointer"
      />
    </div>
  );
}

const RequiredStar = () => <span className="text-red-500 ml-0.5">*</span>;

const FieldError = ({ message }: { message?: string }) =>
  message ? (
    <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
      <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
      {message}
    </p>
  ) : null;

const inputClass = (hasError: boolean, isTouched: boolean, value: any) => {
  const base = "w-full border rounded px-4 py-2 bg-white placeholder-[#575757] transition-colors duration-200 focus:outline-none focus:ring-2";
  if (hasError) return `${base} border-red-400 focus:ring-red-300 focus:border-red-500`;
  if (isTouched && value) return `${base} border-green-400 focus:ring-green-300 focus:border-green-500`;
  return `${base} border-gray-300 focus:ring-primary/30 focus:border-primary`;
};

const selectStyles = (hasError: boolean, isTouched: boolean, hasValue: boolean) => ({
  control: (base: any, state: any) => ({
    ...base,
    borderColor: hasError ? "#f87171" : isTouched && hasValue ? "#4ade80" : state.isFocused ? "var(--color-primary, #6366f1)" : "#d1d5db",
    boxShadow: state.isFocused
      ? hasError ? "0 0 0 2px rgba(248,113,113,0.3)" : "0 0 0 2px rgba(99,102,241,0.3)"
      : "none",
    "&:hover": { borderColor: hasError ? "#f87171" : "#a1a1aa" },
    transition: "border-color 0.2s, box-shadow 0.2s",
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
});

const formatCurrency = (n: number) =>
  "₹" + n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

type Opt = { value: number; label: string };
type Item = { id: number; name: string };

const toOptions = (items: Item[]): Opt[] =>
  items.map((i) => ({ value: i.id, label: i.name }));

async function j<T>(url: string, signal?: AbortSignal): Promise<T> {
  const r = await fetch(url, { headers: { Accept: "application/json" }, signal });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json();
}

// genderOptions moved inside component to use translations

type RecordData = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  quantity: number;
  state_id?: number;
  state_name?: string;
};

type Summary = {
  item_unit_price: number;
  quantity: number;
  subtotal: number;
  cgst_rate: number;
  sgst_rate: number;
  igst_rate: number;
  cgst_amount: number;
  sgst_amount: number;
  igst_amount: number;
  total_gst: number;
  total_with_gst: number;
  prebooking_paid: number;
  payable_amount: number;
  is_maharashtra: boolean;
};

export default function ClientCustomerRegistration() {
  const t = useTranslations("customerRegistration");
  const searchParams = useSearchParams();
  const router = useRouter();
  const recordId = searchParams.get("id");

  const genderOptions = [
    { value: "male", label: t("genderOptions.male") },
    { value: "female", label: t("genderOptions.female") },
    { value: "other", label: t("genderOptions.other") },
  ];

  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState<RecordData | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [pageError, setPageError] = useState("");
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [pendingPaymentUrl, setPendingPaymentUrl] = useState<string | null>(null);

  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  // Fetch prebooking record on mount
  useEffect(() => {
    if (!recordId) {
      setPageError(t("invalidLink"));
      setLoading(false);
      return;
    }

    fetch(`${API_BASE}/api/customer-registration/${recordId}`, {
      headers: { Accept: "application/json" },
    })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok || !data.status) {
          if (data.completed) {
            router.replace("/partner-registration-completed");
            return;
          } else {
            setPageError(data.message || t("registrationNotFound"));
          }
          return;
        }
        setRecord(data.record);
        setSummary(data.summary);
        if (data.already_registered) {
          setAlreadyRegistered(true);
        }
        if (data.pending_payment_url) {
          setPendingPaymentUrl(data.pending_payment_url);
        }
      })
      .catch(() => setPageError(t("failedToLoad")))
      .finally(() => setLoading(false));
  }, [recordId]);

  const RegistrationSchema = useMemo(() => {
    return z.object({
      full_name: z
        .string()
        .min(1, t("validation.fullNameRequired"))
        .max(100, t("validation.fullNameMax")),

      email: z
        .string()
        .min(1, t("validation.emailRequired"))
        .regex(EMAIL_REGEX, t("validation.emailInvalid")),

      mobile: z
        .string()
        .min(1, t("validation.mobileRequired"))
        .regex(MOBILE_REGEX, t("validation.mobileInvalid")),

      sex: z.enum(["male", "female", "other"], {
        message: t("validation.genderRequired"),
      }),

      date: z
        .string()
        .min(1, t("validation.dobRequired"))
        .regex(ISO_DATE_REGEX, t("validation.dobFormat"))
        .refine((val) => {
          const d = new Date(val);
          return !isNaN(d.getTime()) && d <= new Date();
        }, t("validation.dobInvalid"))
        .refine((val) => {
          const dob = new Date(val);
          const today = new Date();
          let age = today.getFullYear() - dob.getFullYear();
          const m = today.getMonth() - dob.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
          return age >= 18;
        }, t("validation.dobMinAge")),

      gst_no: z.preprocess(
        (val) =>
          typeof val === "string" ? val.toUpperCase().trim() : val,
        z
          .string()
          .optional()
          .refine((val) => {
            if (!val || val === "") return true;
            return GST_REGEX.test(val);
          }, t("validation.gstInvalid"))
      ),

      state_id: z
        .any()
        .refine((val) => val !== undefined && val !== null && val !== "" && !Number.isNaN(Number(val)), {
          message: t("validation.stateRequired"),
        })
        .transform((val) => Number(val)),
      district_id: z
        .any()
        .refine((val) => val !== undefined && val !== null && val !== "" && !Number.isNaN(Number(val)), {
          message: t("validation.districtRequired"),
        })
        .transform((val) => Number(val)),
      taluka_id: z
        .any()
        .refine((val) => val !== undefined && val !== null && val !== "" && !Number.isNaN(Number(val)), {
          message: t("validation.talukaRequired"),
        })
        .transform((val) => Number(val)),
      village_id: z
        .any()
        .refine((val) => val !== undefined && val !== null && val !== "" && !Number.isNaN(Number(val)), {
          message: t("validation.villageRequired"),
        })
        .transform((val) => Number(val)),
      address: z
        .string()
        .min(1, t("validation.addressRequired"))
        .max(500, t("validation.addressMax")),
    });
  }, [t]);

  type RegistrationForm = z.infer<typeof RegistrationSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, touchedFields },
    setValue,
    watch,
    control,
    reset,
  } = useForm<RegistrationForm>({
    resolver: zodResolver(RegistrationSchema) as any,
    defaultValues: {
      full_name: "",
      email: "",
      mobile: "",
      sex: undefined as any,
      date: "",
      gst_no: "",
      state_id: undefined as any,
      district_id: undefined as any,
      taluka_id: undefined as any,
      village_id: undefined as any,
      address: "",
    },
    mode: "onTouched",
  });

  // Pre-fill form when record loads
  useEffect(() => {
    if (record) {
      const fullName = [record.first_name, record.last_name].filter(Boolean).join(" ");
      setValue("full_name", fullName);
      setValue("email", record.email || "");
      setValue("mobile", record.mobile || "");
    }
  }, [record, setValue]);

  const stateId = watch("state_id");
  const districtId = watch("district_id");
  const talukaId = watch("taluka_id");
  const addressValue = watch("address") || "";
  const dateValue = watch("date") || "";
  const dobBounds = useMemo(getDobBounds, []);
  const fullNameValue = watch("full_name") || "";
  const emailValue = watch("email") || "";
  const mobileValue = watch("mobile") || "";
  const gstValue = watch("gst_no") || "";

  const [stateOpt, setStateOpt] = useState<Opt | null>(null);
  const [districtOpt, setDistrictOpt] = useState<Opt | null>(null);
  const [talukaOpt, setTalukaOpt] = useState<Opt | null>(null);
  const [villageOpt, setVillageOpt] = useState<Opt | null>(null);

  // Prefill state from prebooking record
  useEffect(() => {
    if (record?.state_id && record?.state_name) {
      setValue("state_id", record.state_id);
      setStateOpt({ value: record.state_id, label: record.state_name });
    }
  }, [record, setValue]);

  const loadStates = async (input: string) => {
    const data: Item[] = await j(`${API_BASE}/api/locations/states`);
    return toOptions(
      data.filter((x) =>
        x.name.toLowerCase().includes(input.toLowerCase())
      )
    );
  };

  const loadDistricts =
    (sid?: number) => async (input: string) => {
      if (!sid) return [];
      const data: Item[] = await j(
        `${API_BASE}/api/locations/districts?state_id=${sid}`
      );
      return toOptions(
        data.filter((x) =>
          x.name.toLowerCase().includes(input.toLowerCase())
        )
      );
    };

  const loadTalukas =
    (did?: number) => async (input: string) => {
      if (!did) return [];
      const data: Item[] = await j(
        `${API_BASE}/api/locations/talukas?district_id=${did}`
      );
      return toOptions(
        data.filter((x) =>
          x.name.toLowerCase().includes(input.toLowerCase())
        )
      );
    };

  const loadVillages =
    (tid?: number) => async (input: string) => {
      if (!tid) return [];
      const data: Item[] = await j(
        `${API_BASE}/api/locations/villages?taluka_id=${tid}`
      );
      return toOptions(
        data.filter((x) =>
          x.name.toLowerCase().includes(input.toLowerCase())
        )
      );
    };

  const onSubmit = async (values: RegistrationForm) => {
    if (!recaptchaToken) {
      toast.error(t("validation.captchaRequired"));
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE}/api/customer-registration/${recordId}/submit-and-pay`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            ...values,
            recaptcha_token: recaptchaToken,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok || !data.status) {
        if (data.errors) {
          const errorMessages = Object.values(data.errors).flat();
          errorMessages.forEach((msg: any) => toast.error(msg));
        } else {
          toast.error(data.message || t("toast.somethingWrong"));
        }
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
        return;
      }

      if (data.short_url && /^https:\/\/rzp\.io\/rzp\/[a-zA-Z0-9]+$/.test(data.short_url)) {
        trackRegistrationFormSubmitted({ user_type: "user", page: "customer_registration" });
        trackRegistrationPaymentInitiated({ user_type: "user", page: "customer_registration" });
        toast.success(t("toast.redirectingPayment"));
        window.location.href = data.short_url;
      }
    } catch (err: any) {
      toast.error(err.message ?? t("toast.somethingWrong"));
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    }
  };

  // Loading state
  if (loading) {
    return (
      <section className="section-padding bg-white py-24">
        <Breadcrumb
          pageName={t("breadcrumb")}
          description=""
          bgImages={[getImagePath("/images/breadcrumb/aboutUs.webp")]}
          bgIndex={0}
        />
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">{t("loadingDetails")}</p>
        </div>
      </section>
    );
  }

  // Error state
  if (pageError) {
    return (
      <section className="section-padding bg-white py-24">
        <Breadcrumb
          pageName={t("breadcrumb")}
          description=""
          bgImages={[getImagePath("/images/breadcrumb/aboutUs.webp")]}
          bgIndex={0}
        />
        <div className="container mx-auto px-4 py-16 text-center max-w-lg">
          <div className="bg-red-50 border border-red-200 rounded-xl p-8">
            <svg className="w-12 h-12 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h3 className="text-lg font-semibold text-red-700 mb-2">{t("registrationUnavailable")}</h3>
            <p className="text-red-600">{pageError}</p>
          </div>
        </div>
      </section>
    );
  }


  // Already registered but payment pending — show payment link
  if (alreadyRegistered && pendingPaymentUrl) {
    return (
      <section className="section-padding bg-white py-24">
        <Breadcrumb
          pageName={t("breadcrumb")}
          description=""
          bgImages={[getImagePath("/images/breadcrumb/aboutUs.webp")]}
          bgIndex={0}
        />
        <div className="container mx-auto px-4 py-16 text-center max-w-lg">
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-8">
            <svg className="w-12 h-12 text-orange-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-semibold text-orange-700 mb-2">{t("paymentPending")}</h3>
            <p className="text-orange-600 mb-4">{t("paymentPendingDesc")}</p>
            <a
              href={pendingPaymentUrl}
              className="inline-block bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-3 rounded-lg text-lg shadow-md hover:shadow-lg transition-all duration-200"
            >
              {t("completePayment")}
            </a>
          </div>
        </div>
      </section>
    );
  }

  const s = summary;

  return (
    <section className="section-padding bg-white py-24">
      <Breadcrumb
        pageName={t("breadcrumb")}
        description=""
        bgImages={[getImagePath("/images/breadcrumb/aboutUs.webp")]}
        bgIndex={0}
      />

      <div className="container mx-auto px-4 py-6 max-w-5xl">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Side: Registration Form */}
          <div className="flex-1">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <div className="bg-primary px-6 py-4">
                <h3 className="text-white font-semibold text-lg">{t("pageTitle")}</h3>
              </div>
              <div className="p-6">
                <form
                  noValidate
                  onSubmit={handleSubmit(
                    async (values: any) => {
                      await onSubmit(values);
                    },
                    (fieldErrors) => {
                      const errorCount = Object.keys(fieldErrors).length;
                      toast.error(
                        t("validation.formErrors", { count: errorCount })
                      );
                      const firstErrorField = document.querySelector("[class*='border-red']");
                      firstErrorField?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }
                  )}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Full Name — Pre-filled, Read-only */}
                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-[#575757]">
                        {t("labels.fullName")} <RequiredStar />
                      </label>
                      <input
                        type="text"
                        {...register("full_name")}
                        className={`${inputClass(!!errors.full_name, !!touchedFields.full_name, fullNameValue)} rounded-lg bg-gray-50`}
                        placeholder={t("placeholders.fullName")}
                        maxLength={100}
                        readOnly
                      />
                      <FieldError message={errors.full_name?.message as string} />
                    </div>

                    {/* Email — Pre-filled, Read-only */}
                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-[#575757]">
                        {t("labels.email")} <RequiredStar />
                      </label>
                      <input
                        type="email"
                        {...register("email")}
                        className={`${inputClass(!!errors.email, !!touchedFields.email, emailValue)} rounded-lg bg-gray-50`}
                        placeholder={t("placeholders.email")}
                        readOnly
                      />
                      <FieldError message={errors.email?.message as string} />
                    </div>

                    {/* Mobile — Pre-filled, Read-only */}
                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-[#575757]">
                        {t("labels.mobile")} <RequiredStar />
                      </label>
                      <input
                        type="text"
                        {...register("mobile")}
                        className={`${inputClass(!!errors.mobile, !!touchedFields.mobile, mobileValue)} rounded-lg bg-gray-50`}
                        placeholder={t("placeholders.mobile")}
                        maxLength={10}
                        readOnly
                      />
                      <FieldError message={errors.mobile?.message as string} />
                    </div>

                    {/* Gender */}
                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-[#575757]">
                        {t("labels.gender")} <RequiredStar />
                      </label>
                      <Controller
                        name="sex"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={genderOptions}
                            placeholder={t("placeholders.gender")}
                            isClearable
                            classNamePrefix="react-select"
                            styles={selectStyles(!!errors.sex, !!touchedFields.sex, !!field.value)}
                            menuPortalTarget={typeof window !== "undefined" ? document.body : undefined}
                            value={
                              genderOptions.find(
                                (opt) => opt.value === field.value
                              ) || null
                            }
                            onChange={(opt) =>
                              field.onChange((opt as any)?.value || "")
                            }
                          />
                        )}
                      />
                      <FieldError message={errors.sex?.message as string} />
                    </div>

                    {/* Date of Birth */}
                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-[#575757]">
                        {t("labels.dateOfBirth")} <RequiredStar />
                      </label>
                      <Controller
                        name="date"
                        control={control}
                        render={({ field }) => (
                          <DateField
                            value={field.value || ""}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            min={dobBounds.min}
                            max={dobBounds.max}
                            placeholder="DD-MM-YYYY"
                            className={`${inputClass(!!errors.date, !!touchedFields.date, dateValue)} rounded-lg`}
                          />
                        )}
                      />
                      <FieldError message={errors.date?.message as string} />
                    </div>

                    {/* GST No (Optional) */}
                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-[#575757]">
                        {t("labels.gstNo")} <span className="text-gray-400 text-xs">({t("labels.optional")})</span>
                      </label>
                      <input
                        {...register("gst_no", {
                          onChange: (e) => {
                            e.target.value = e.target.value.toUpperCase();
                          },
                        })}
                        type="text"
                        className={`${inputClass(!!errors.gst_no, !!touchedFields.gst_no, gstValue)} uppercase rounded-lg`}
                        placeholder="27ABCDE1234F1Z5"
                        maxLength={15}
                      />
                      <FieldError message={errors.gst_no?.message as string} />
                    </div>

                    {/* State */}
                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-[#575757]">
                        {t("labels.state")} <RequiredStar />
                      </label>
                      <Controller
                        name="state_id"
                        control={control}
                        render={({ field: { onChange } }) => (
                          <AsyncSelect
                            key="state"
                            cacheOptions
                            defaultOptions
                            isClearable={!record?.state_id}
                            isDisabled={!!record?.state_id}
                            placeholder={t("placeholders.state")}
                            loadOptions={loadStates}
                            value={stateOpt}
                            onChange={(opt) => {
                              const o = (opt as any) || null;
                              setStateOpt(o);
                              onChange(o ? o.value : undefined);
                              setDistrictOpt(null);
                              setTalukaOpt(null);
                              setVillageOpt(null);
                              setValue("district_id", undefined as any);
                              setValue("taluka_id", undefined as any);
                              setValue("village_id", undefined as any);
                            }}
                            classNamePrefix="react-select"
                            styles={selectStyles(!!errors.state_id, !!touchedFields.state_id, !!stateOpt)}
                            menuPortalTarget={typeof window !== "undefined" ? document.body : undefined}
                          />
                        )}
                      />
                      <FieldError message={errors.state_id?.message as string} />
                    </div>

                    {/* District */}
                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-[#575757]">
                        {t("labels.district")} <RequiredStar />
                      </label>
                      <Controller
                        name="district_id"
                        control={control}
                        render={({ field: { onChange } }) => (
                          <AsyncSelect
                            key={`district-${stateId || "none"}`}
                            cacheOptions
                            defaultOptions
                            isClearable
                            isDisabled={!stateId}
                            placeholder={stateId ? t("placeholders.district") : t("placeholders.districtFirst")}
                            loadOptions={loadDistricts(stateId || undefined)}
                            value={districtOpt}
                            onChange={(opt) => {
                              const o = (opt as any) || null;
                              setDistrictOpt(o);
                              onChange(o ? o.value : undefined);
                              setTalukaOpt(null);
                              setVillageOpt(null);
                              setValue("taluka_id", undefined as any);
                              setValue("village_id", undefined as any);
                            }}
                            classNamePrefix="react-select"
                            styles={selectStyles(!!errors.district_id, !!touchedFields.district_id, !!districtOpt)}
                            menuPortalTarget={typeof window !== "undefined" ? document.body : undefined}
                          />
                        )}
                      />
                      <FieldError message={errors.district_id?.message as string} />
                    </div>

                    {/* Taluka */}
                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-[#575757]">
                        {t("labels.taluka")} <RequiredStar />
                      </label>
                      <Controller
                        name="taluka_id"
                        control={control}
                        render={({ field: { onChange } }) => (
                          <AsyncSelect
                            key={`taluka-${districtId || "none"}`}
                            cacheOptions
                            defaultOptions
                            isClearable
                            isDisabled={!districtId}
                            placeholder={districtId ? t("placeholders.taluka") : t("placeholders.talukaFirst")}
                            loadOptions={loadTalukas(
                              Number.isNaN(Number(districtId))
                                ? undefined
                                : Number(districtId)
                            )}
                            value={talukaOpt}
                            onChange={(opt) => {
                              const o = (opt as any) || null;
                              setTalukaOpt(o);
                              onChange(o ? o.value : NaN);
                              setVillageOpt(null);
                              setValue("village_id", NaN as any);
                            }}
                            classNamePrefix="react-select"
                            styles={selectStyles(!!errors.taluka_id, !!touchedFields.taluka_id, !!talukaOpt)}
                            menuPortalTarget={typeof window !== "undefined" ? document.body : undefined}
                          />
                        )}
                      />
                      <FieldError message={errors.taluka_id?.message as string} />
                    </div>

                    {/* Village */}
                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-[#575757]">
                        {t("labels.village")} <RequiredStar />
                      </label>
                      <Controller
                        name="village_id"
                        control={control}
                        render={({ field: { onChange } }) => (
                          <AsyncSelect
                            key={`village-${talukaId || "none"}`}
                            cacheOptions
                            defaultOptions
                            isClearable
                            isDisabled={!talukaId}
                            placeholder={talukaId ? t("placeholders.village") : t("placeholders.villageFirst")}
                            loadOptions={loadVillages(
                              Number.isNaN(Number(talukaId))
                                ? undefined
                                : Number(talukaId)
                            )}
                            value={villageOpt}
                            onChange={(opt) => {
                              const o = (opt as any) || null;
                              setVillageOpt(o);
                              onChange(o ? o.value : NaN);
                            }}
                            classNamePrefix="react-select"
                            styles={selectStyles(!!errors.village_id, !!touchedFields.village_id, !!villageOpt)}
                            menuPortalTarget={typeof window !== "undefined" ? document.body : undefined}
                          />
                        )}
                      />
                      <FieldError message={errors.village_id?.message as string} />
                    </div>

                    {/* Address - Full Width */}
                    <div className="md:col-span-2">
                      <label className="block mb-1.5 text-sm font-medium text-[#575757]">
                        {t("labels.address")} <RequiredStar />
                      </label>
                      <textarea
                        {...register("address")}
                        rows={2}
                        className={`${inputClass(!!errors.address, !!touchedFields.address, addressValue)} rounded-lg`}
                        placeholder={t("placeholders.address")}
                        maxLength={500}
                      />
                      <div className="flex justify-between items-center mt-1">
                        <FieldError message={errors.address?.message as string} />
                        <span className={`text-xs ml-auto ${addressValue.length > 450 ? "text-orange-500" : "text-gray-400"}`}>
                          {addressValue.length}/500
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-start mt-4">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
                      onChange={(token) => setRecaptchaToken(token)}
                    />
                  </div>

                  <div className="flex justify-center pt-4">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-primary hover:bg-primary/90 text-white font-semibold px-10 py-3 rounded-lg text-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          {t("buttons.processing")}
                        </span>
                      ) : (
                        t("buttons.submitAndPay")
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Right Side: Dynamic Payment Summary */}
          <div className="lg:w-[340px] w-full">
            <div className="lg:sticky lg:top-8">
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <div className="bg-primary px-6 py-4">
                  <h3 className="text-white font-semibold text-lg">{t("paymentSummary")}</h3>
                </div>
                {s ? (
                  <div className="p-6 space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t("summary.kstStick")}</span>
                      <span className="font-medium">{formatCurrency(s.item_unit_price)}</span>
                    </div>
                    <hr className="border-gray-200" />
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t("summary.subtotal")}</span>
                      <span className="font-medium">{formatCurrency(s.subtotal)}</span>
                    </div>

                    {s.is_maharashtra ? (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t("summary.cgst")} ({s.cgst_rate}%)</span>
                          <span>{formatCurrency(s.cgst_amount)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t("summary.sgst")} ({s.sgst_rate}%)</span>
                          <span>{formatCurrency(s.sgst_amount)}</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t("summary.igst")} ({s.igst_rate}%)</span>
                        <span>{formatCurrency(s.igst_amount)}</span>
                      </div>
                    )}

                    <div className="flex justify-between">
                      <span className="text-gray-600">{t("summary.totalGst")}</span>
                      <span>{formatCurrency(s.total_gst)}</span>
                    </div>
                    <hr className="border-gray-200" />
                    <div className="flex justify-between font-medium text-base">
                      <span>{t("summary.totalInclGst")}</span>
                      <span>{formatCurrency(s.total_with_gst)}</span>
                    </div>
                    <div className="flex justify-between text-green-600">
                      <span>{t("summary.preBookingPaid")}</span>
                      <span>- {formatCurrency(s.prebooking_paid)}</span>
                    </div>
                    <hr className="border-gray-200" />
                    <div className="flex justify-between bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 text-base font-bold text-orange-700">
                      <span>{t("summary.amountToPay")}</span>
                      <span>{formatCurrency(s.payable_amount)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 text-center text-gray-400">
                    <p>{t("loadingSummary")}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
